import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { Modal, PrimaryButton } from '..'
import { logout, setTimer } from '../../store/slices'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { isSessionKeepAliveActive } from '../../utils/sessionKeepAlive'
import { invalidateFavoriteDestinationsCache } from '../../utils/favoriteDestinationsCache'

/**
 * Exportadas para que los tests deriven de acá en vez de hardcodear segundos.
 * Ya pasó una vez: el test de re-login fijaba 120 s, alguien subió la sesión a
 * 20 min, y el test quedó rojo sin que nada del producto estuviera mal.
 */
export const LOGOUT_TIME = 20 * 60 // 20 minutos de sesión
export const WARNING_TIME = 2 * 60 // el modal aparece 2 min antes de cerrar
const ACTIVITY_SYNC_KEY = 'logoutTimer:lastActivity'

/**
 * Cada cuánto se refresca el timer mientras alguna pestaña exige sesión viva
 * (carga masiva). No se refresca en cada tick: eso serían 60 dispatches y 60
 * escrituras de redux-persist por minuto y por pestaña, para nada. Una vez por
 * minuto alcanza — el timer persistido queda a lo sumo 60 s viejo, así que tras
 * un F5 la sesión arranca con ~19 min por delante.
 */
const KEEP_ALIVE_REFRESH_SECONDS = 60

export const LogoutTimer: FC = () => {
  const dispatch = useAppDispatch()
  const timer = useAppSelector((state) => state.user.timer)
  const logged = useAppSelector((state) => state.user.logged)

  const timerRef = useRef(timer)
  const [remainingTime, setRemainingTime] = useState(LOGOUT_TIME)
  const [openLogoutModal, setOpenLogoutModal] = useState(false)

  const computeRemaining = useCallback(
    () => LOGOUT_TIME - (new Date().getTime() / 1000 - timerRef.current),
    []
  )

  const extendSession = useCallback(
    (currentTime: number, broadcast: boolean) => {
      timerRef.current = currentTime
      setRemainingTime(LOGOUT_TIME)
      setOpenLogoutModal(false)
      dispatch(setTimer(currentTime))

      if (broadcast) {
        try {
          localStorage.setItem(
            ACTIVITY_SYNC_KEY,
            JSON.stringify({ time: currentTime, nonce: Math.random() })
          )
        } catch {}
      }
    },
    [dispatch]
  )

  useEffect(() => {
    if (!logged) return

    const intervalId = setInterval(() => {
      // Si alguna pestaña (esta u otra) está en carga masiva, la sesión no
      // cuenta hacia atrás: se empuja el timer hacia adelante y se propaga al
      // resto, así ninguna de las otras pestañas muestra el aviso.
      const nowSeconds = new Date().getTime() / 1000
      if (
        isSessionKeepAliveActive() &&
        nowSeconds - timerRef.current >= KEEP_ALIVE_REFRESH_SECONDS
      ) {
        extendSession(nowSeconds, true)
        return
      }
      setRemainingTime(computeRemaining())
    }, 1000)

    return () => clearInterval(intervalId)
  }, [logged, computeRemaining, extendSession])

  useEffect(() => {
    if (!logged) {
      setOpenLogoutModal(false)
      setRemainingTime(LOGOUT_TIME)
      return
    }
    // Segunda barrera, por si el tick que refresca el timer no llegó a correr
    // (pestaña recién despertada de un suspend, con un remainingTime viejo y ya
    // negativo en memoria). Mientras haya arriendo no se avisa ni se cierra.
    if (isSessionKeepAliveActive()) {
      setOpenLogoutModal(false)
      return
    }
    if (
      remainingTime > 0 &&
      remainingTime <= WARNING_TIME &&
      !openLogoutModal
    ) {
      setOpenLogoutModal(true)
    } else if (remainingTime <= 0) {
      setOpenLogoutModal(false)
      // La libreta son datos de terceros y el equipo comparte
      // equipos: se vacía entera antes de cerrar sesión.
      invalidateFavoriteDestinationsCache()
      dispatch(logout())
    }
    // Don't add openLogoutModal to the dependencies array because it will cause an
    // infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remainingTime, dispatch, logged])

  useEffect(() => {
    timerRef.current = timer
  }, [timer])

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return
      // Volver del segundo plano con una carga masiva abierta no puede saldar
      // la deuda de tiempo acumulada mientras la pestaña estuvo congelada.
      if (isSessionKeepAliveActive()) {
        extendSession(new Date().getTime() / 1000, true)
        return
      }
      setRemainingTime(computeRemaining())
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [computeRemaining, extendSession])

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== ACTIVITY_SYNC_KEY || !e.newValue) return
      try {
        const { time } = JSON.parse(e.newValue)
        if (typeof time === 'number') extendSession(time, false)
      } catch {}
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [extendSession])

  const handleCloseModal = () => {
    extendSession(new Date().getTime() / 1000, true)
  }

  return (
    <Modal
      open={openLogoutModal && logged}
      setOpen={() => {}}
      className="flex flex-1 flex-col"
      style={{ maxWidth: '28rem' }}
    >
      <div className="flex flex-1 gap-6 items-center">
        <div className="w-10 h-10">
          <InformationCircleIcon className="w-10 h-10 text-main-500" />
        </div>
        <span>
          La sesión finalizará en{' '}
          <b>{Math.max(0, remainingTime).toFixed(0)} segundos</b>. Presione
          continuar para extender el tiempo de la sesión.
        </span>
      </div>

      <hr className="w-full my-4" />

      <div className="flex flex-1 w-full justify-end">
        <PrimaryButton onClick={handleCloseModal}>Continuar</PrimaryButton>
      </div>
    </Modal>
  )
}
